/* eslint-disable no-restricted-syntax */
import React, { useEffect, useReducer, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import "./RequestForm.scss";
import { convertDraft, getDraft, updateDraft } from "api";
import errorToast from "helpers/errorToast";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import usePermissions from "hooks/usePermissions";
import { parseDraftValues, modifyDraftData, resolveServerErrors, hasServerError } from "helpers/requestFormHelper";
import ConfirmationDialog from "components/organisms/ConfirmationDialog/ConfirmationDialog";
import OverlayLoader from "components/atoms/OverlayLoader/OverlayLoader";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import { quickRequestQueryKeys } from "api/queries/quick-request/quickRequestQueryKeys";
import { initialState, reducer, RequestFormActionType } from "./RequestFormState";
import Header from "./components/Header/Header";
import OrganisationForm from "./components/Organisation/OrganisationForm";
import ScheduleForm from "./components/Schedule/ScheduleForm";
import PackingListForm from "./components/PackingList/PackingListForm";
import ServiceForm from "./components/Service/ServiceForm";
import ProviderForm from "./components/Provider/ProviderForm";
import FormConfirmation from "./components/Confirmation/FormConfirmation";
import FormTooltip from "./components/Common/FormTooltip";
import { miniTenderScheduleDefaultValues } from "./components/Schedule/ScheduleDefaultOptions";
import useFormPrefill from "./useFormPrefill";
import { AddressProvider } from "../RequestForm1/components/Schedule/AddressProvider";
import {
  countPackingListRows,
  flattenApiErrors,
  flattenFieldErrors,
  resolveFirstActiveSection,
  trackFormReentry,
  trackQuoteFormErrorLogged,
  trackQuoteFormSubmitted,
} from "./formEvents/requestFormAnalytics";
import { useRequestFormAnalyticsSession } from "./formEvents/useRequestFormAnalyticsSession";

type RequestFormProps = {
  isQuoteRequest: boolean;
  isEditShipment?: boolean;
  isEditQuoteRequest?: boolean;
  isMiniTender?: boolean;
  isQuickRequest?: boolean;
};

function RequestForm(props: RequestFormProps) {
  const { isQuoteRequest, isEditShipment, isMiniTender, isQuickRequest, isEditQuoteRequest } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const { shipmentId } = useParams();
  const queryClient = useQueryClient();
  // const location: any = useLocation();
  // const formData = location.state?.formData;
  const intl = useIntl();
  const { data: shipmentData, isInitialLoading: isLoadingShipmentBasic }: any = useGetShipmentBasic(shipmentId!, {
    enabled: !!shipmentId,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [unsavedStep, setUnsavedStep] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const quotesAvailable = searchParams.get("quotesAvailable");
  const draftId = searchParams.get("draft_id");
  const isDuplicated = searchParams.get("is_duplicated");
  const isShipment = Boolean(shipmentData?.shipment?.shipment_status);
  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      // packing_list: initialPackingList(isMiniTender),
      ...(isMiniTender && {
        schedule: miniTenderScheduleDefaultValues,
      }),
      ...(isQuoteRequest && {
        invited_providers: {
          providers: [],
        },
        invited_modes: {},
      }),
    },
  });
  const { updatePrefillReset } = useFormPrefill(methods, dispatch);

  const { getValues, handleSubmit, watch } = methods;
  const { permissions } = usePermissions();
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const shouldValidateSections = useRef(false);
  const navigate = useNavigate();
  const formStepRefs = {
    [FormStepTypes.ORGANISATION]: useRef<any>(null),
    [FormStepTypes.SCHEDULE]: useRef<any>(null),
    [FormStepTypes.SERVICE]: useRef<any>(null),
    [FormStepTypes.PROVIDER]: useRef<any>(null),
  };
  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};
  const isConsolidatedShipment = watch("shipment.shipment_type") === 2 || false;

  useRequestFormAnalyticsSession({
    enabled: isQuoteRequest,
    isEditQuoteRequest,
    isEditShipment,
    activeStep: currentActiveStep,
  });

  useEffect(() => {
    if (isQuoteRequest && !JSON.parse(permissions || "{}")?.permissions?.quoting?.create_quote_requests)
      navigate("/access-denied");
    if (!isQuoteRequest && !JSON.parse(permissions || "{}")?.permissions?.quoting?.create_quick_requests)
      navigate("/access-denied");
  }, [isQuoteRequest]);

  useEffect(() => {
    if (!currentActiveStep) {
      searchParams.set("active_step", FormStepTypes.ORGANISATION);
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    if (!draftId || getValues("organizational.location")) {
      return;
    }
    setIsLoading(true);
    shouldValidateSections.current = true;
    getDraft(draftId, !isQuoteRequest)
      .then((res) => {
        if (Object.keys(res.data).length) {
          const draftValues = modifyDraftData(res.data, isDuplicated, isQuoteRequest, enable_new_address_structure);
          methods.reset(draftValues);
          updatePrefillReset();
          setPackingListGlobalConfigurationDefaultValues(res.data.packing_list);
          if (isQuoteRequest) {
            trackFormReentry({
              draftId,
              firstFieldActive: resolveFirstActiveSection(draftValues as Record<string, unknown>),
            });
          }
        }
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [draftId, isQuoteRequest, isDuplicated]);

  // Set the default values for the packing list global configuration i.e stackable, hazardous, crating required etc
  const setPackingListGlobalConfigurationDefaultValues = (packingListData: any) => {
    // Map the fields and their relevant value field
    const fieldMappings = [
      { field: "stackable" },
      { field: "hazardous" },
      { field: "crating_required", value: "crating_required_by" },
      { field: "is_dangerous_good", value: "dangerous_good_value" },
      { field: "is_dangerous_good", value: "un_number" },
      {
        field: "temperature_control_required",
        value: "temperature_control_value",
      },
      { field: "handling_required" },
    ];

    const globalConfig = "packing.global_configuration";

    // If the packing list data has any of the above fields, then set the global configuration fields
    fieldMappings.forEach((mapping) => {
      const hasOption = packingListData.some((item: any) => item[mapping.field]);
      methods.setValue(`${globalConfig}.${mapping.field}`, hasOption);
      if (hasOption && mapping.value) {
        const newValue = packingListData.find((item: any) => item[mapping.field])[mapping.value];
        methods.setValue(`${globalConfig}.${mapping.value}`, newValue);
        if (mapping.value === "temperature_control_value" && newValue === "6") {
          methods.setValue(
            `${globalConfig}.temperature_controlled_custom_value`,
            packingListData.find((item: any) => item[mapping.field])?.temperature_controlled_custom_value,
          );
        }
      }
    });

    // Set the unit of measurement and weight for the global configuration
    methods.setValue(`${globalConfig}.w_unit`, packingListData[0]?.w_unit);
    methods.setValue(`${globalConfig}.m_unit`, packingListData[0]?.m_unit);
  };

  const validateSection = async (sectionName: string) => {
    let isCustomValidated = true;
    if (formStepRefs[sectionName]?.current?.checkStepValidation) {
      isCustomValidated = formStepRefs[sectionName].current.checkStepValidation();
    }
    let isValid = true;
    if (FormStepValidationTypes[sectionName] && !hasServerError(methods.formState.errors)) {
      isValid = await methods.trigger(FormStepValidationTypes[sectionName]);
    }
    return isValid && isCustomValidated;
  };

  const updateSectionValidation = async (sectionName: string) => {
    const isValid = await validateSection(sectionName);
    if (isValid) {
      dispatch({
        type: RequestFormActionType.UPDATE_DRAFT_SAVE,
        payload: { key: sectionName, value: true },
      });
    }
  };

  const handleDraftSave = async () => {
    if (currentActiveStep) {
      if (formStepRefs[currentActiveStep]?.current?.checkStepValidation) {
        formStepRefs[currentActiveStep].current.checkStepValidation();
      }
      let isValid = true;
      if (FormStepValidationTypes[currentActiveStep] && !hasServerError(methods.formState.errors)) {
        isValid = await methods.trigger(FormStepValidationTypes[currentActiveStep]);
      }
      if (isValid && draftId && getValues("organizational.location")) {
        return updateDraft(parseDraftValues(getValues()), draftId, !isQuoteRequest)
          .then(() => {
            dispatch({
              type: RequestFormActionType.UPDATE_DRAFT_SAVE,
              payload: { key: currentActiveStep, value: true },
            });
            handleSuccessfullyDraftSaved();
          })
          .catch((err) => {
            resolveServerErrors(err, { ...getValues() }, methods.setError);
          });
      }
    }
  };

  const handleSuccessfullyDraftSaved = () => {
    if (currentActiveStep === FormStepTypes.ORGANISATION) {
      if (isQuoteRequest) {
        formStepRefs[FormStepTypes.PROVIDER].current?.refetchProviders();
      }
      if (isDuplicated) {
        searchParams.delete("is_duplicated");
        setSearchParams(searchParams);
      }
    }
  };

  const handleAccordionChange = (eventKey: string | null) => {
    handleDraftSave();

    if (!eventKey) {
      searchParams.delete("active_step");
    } else {
      searchParams.set("active_step", eventKey);
    }
    setSearchParams(searchParams);
  };

  const validateStepAndFocusOnError = async () => {
    let isValidate = true;
    const stepPriorityOrder = [
      FormStepTypes.ORGANISATION,
      FormStepTypes.SCHEDULE,
      FormStepTypes.PACKING_LIST,
      FormStepTypes.SERVICE,
      FormStepTypes.PROVIDER,
    ];
    for (const stepName of stepPriorityOrder) {
      // eslint-disable-next-line no-await-in-loop
      const isValid = await validateSection(stepName);
      if (!isValid) {
        handleAccordionChange(stepName);
        isValidate = false;
        break;
      }
    }
    if (!isValidate && isQuoteRequest) {
      trackQuoteFormErrorLogged(flattenFieldErrors(methods.formState.errors));
    }
    return isValidate;
  };

  const checkUnsavedStepsInForm = () => {
    let hasUnsavedStep = false;
    const formSteps = Object.keys(state.unsavedChanges);
    for (const step of formSteps) {
      if (state.unsavedChanges[step]) {
        hasUnsavedStep = true;
        setUnsavedStep(step);
        break;
      }
    }
    return hasUnsavedStep;
  };

  const handleRequestSubmit = async () => {
    setIsSubmitting(true);
    await handleDraftSave();
    convertDraft(draftId!, !isQuoteRequest)
      .then((res) => {
        if (isQuoteRequest) {
          const packingCounts = countPackingListRows(getValues("packing_list"));
          const invitedProviders = getValues("invited_providers.providers") || [];
          trackQuoteFormSubmitted({
            providerCount: Array.isArray(invitedProviders) ? invitedProviders.length : 0,
            totalContainers: packingCounts.total_containers,
            totalCommodities: packingCounts.total_commodities,
          });

          const isParcelOrRateCardAvailable =
            state.providers?.invited_providers?.some((provider: any) => provider.rate_card_available) ||
            getValues("invited_modes").parcel;

          let path = `/shipment/${res.data.shipment_id}`;
          // If parcel or rate card is available, then we pass the pull_quotes query param to the quote page
          // so that the quote page can pull the quotes on a timeout basis to fetch the autogenerated quotes
          if (isParcelOrRateCardAvailable) {
            path = `/shipment/${res.data.shipment_id}?pull_quotes=true`;
          }
          // while editing rate card quotess after submitting BE invalidate cache for quotations and we
          // request quottaion at the same time causing 500 error, so redirecting with a delay.
          if (isEditQuoteRequest && shipmentId && quotesAvailable === "true") {
            setTimeout(() => {
              setIsSubmitting(false);
              navigate(path);
            }, 5000);
          } else {
            navigate(path);
          }
        } else {
          if (shipmentId) {
            queryClient.invalidateQueries({ queryKey: quickRequestQueryKeys.quickRequestDetails(shipmentId) });
          }
          navigate(`/shipment/${res.data.quick_request_id}/quick`);
        }
      })
      .catch((err) => {
        if (isQuoteRequest) {
          trackQuoteFormErrorLogged(flattenApiErrors(err));
        }
        resolveServerErrors(err, { ...getValues() }, methods.setError);
        errorToast(err);
      })
      .finally(() => {
        if (!isEditQuoteRequest || !shipmentId || !quotesAvailable || quotesAvailable === "false") {
          setIsSubmitting(false);
        }
      });
  };

  const onSubmit = async () => {
    const isValid = await validateStepAndFocusOnError();
    if (!isValid) {
      return;
    }
    if (checkUnsavedStepsInForm()) {
      setShowConfirmationDialog(true);
    } else {
      handleRequestSubmit();
    }
  };

  const onError = () => {
    validateStepAndFocusOnError();
  };

  const handleSubmitClick = () => {
    if (submitBtnRef.current) {
      submitBtnRef.current.click();
    }
  };

  const handleUnsavedSectionReview = () => {
    if (!unsavedStep) return;
    setShowConfirmationDialog(false);
    handleAccordionChange(unsavedStep);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="request-form"
        id="request-form"
        noValidate
        data-testid={testIds.REQUEST_FORM_CONTAINER}
      >
        <Header
          state={state}
          isQuoteRequest={isQuoteRequest}
          isEditShipment={isEditShipment}
          isMiniTender={isMiniTender}
          isQuickRequest={isQuickRequest}
        />
        <Accordion
          onSelect={handleAccordionChange}
          activeKey={currentActiveStep || ""}
          data-testid={testIds.REQUEST_FORM_ACCORDION}
        >
          <OrganisationForm
            state={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
            isMiniTender={isMiniTender}
            childRef={formStepRefs[FormStepTypes.ORGANISATION]}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
            isLoadingShipmentBasic={isLoadingShipmentBasic}
            isShipment={isShipment}
            disablePaymentLocation={isConsolidatedShipment}
          />
          <AddressProvider>
            <ScheduleForm
              formState={state}
              dispatch={dispatch}
              isMiniTender={isMiniTender}
              isQuoteRequest={isQuoteRequest}
              validateSection={updateSectionValidation}
              shouldValidateSection={shouldValidateSections}
              childRef={formStepRefs[FormStepTypes.SCHEDULE]}
            />
          </AddressProvider>
          {!isConsolidatedShipment && (
            <PackingListForm
              state={state}
              dispatch={dispatch}
              validateSection={updateSectionValidation}
              shouldValidateSection={shouldValidateSections}
              isQuoteRequest={isQuoteRequest}
            />
          )}
          <ServiceForm
            state={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
            handleDraftSave={handleDraftSave}
            childRef={formStepRefs[FormStepTypes.SERVICE]}
          />
          {isQuoteRequest && (
            <ProviderForm
              state={state}
              dispatch={dispatch}
              isMiniTender={isMiniTender}
              childRef={formStepRefs[FormStepTypes.PROVIDER]}
              validateSection={updateSectionValidation}
              shouldValidateSection={shouldValidateSections}
              isShipment={isShipment}
              handleDraftSave={handleDraftSave}
            />
          )}
          <FormConfirmation
            isSubmitting={isSubmitting}
            state={state}
            isQuoteRequest={isQuoteRequest}
            onSubmitClick={handleSubmitClick}
          />
        </Accordion>
        <button
          ref={submitBtnRef}
          type="submit"
          className="form-submit"
          data-testid={testIds.REQUEST_FORM_HIDDEN_SUBMIT_BUTTON}
        >
          submit
        </button>
        {isLoading && <OverlayLoader />}
        {Object.entries(state.tooltipFields).map(([name, message]) => (
          <FormTooltip key={`${name}-${message}`} name={name} message={message} />
        ))}
        <ConfirmationDialog
          open={showConfirmationDialog}
          onClose={() => setShowConfirmationDialog(false)}
          onSecondaryBtnClick={() => {
            setShowConfirmationDialog(false);
            handleRequestSubmit();
          }}
          onSubmit={handleUnsavedSectionReview}
          title={
            unsavedStep === FormStepTypes.PACKING_LIST
              ? intl.formatMessage({
                  id: "new-request.form.packingListConfirmationTitle",
                  defaultMessage: "Packing list items not keyed in",
                })
              : intl.formatMessage(
                  {
                    id: "new-request.form.confirmationTitle",
                    defaultMessage: "Submit your {request} request?",
                  },
                  { request: isQuoteRequest ? "quote" : "quick" },
                )
          }
          description={
            unsavedStep === FormStepTypes.PACKING_LIST
              ? intl.formatMessage({
                  id: "new-request.form.packingListConfirmationDescription",
                  defaultMessage: "Are you sure you want to proceed without entering packing list items?",
                })
              : intl.formatMessage(
                  {
                    id: "new-request.form.confirmationDescription",
                    defaultMessage: "You have not added details in the {unsavedStep} section",
                  },
                  { unsavedStep },
                )
          }
          submitBtnText={intl.formatMessage({
            id: "new-request.form.confirmationPrimaryBtnText",
            defaultMessage: "Go back and add items",
          })}
          secondaryBtnText={intl.formatMessage({
            id: "new-request.form.confirmationSecondaryBtnText",
            defaultMessage: "Proceed without items",
          })}
        />
      </form>
    </FormProvider>
  );
}

export default RequestForm;

RequestForm.defaultProps = {
  isEditShipment: false,
  isEditQuoteRequest: false,
  isMiniTender: false,
  isQuickRequest: false,
};
